import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AdminLogin } from '../components/AdminLogin';
import { AdminDashboard } from '../components/AdminDashboard';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { Loader2, LogOut, Shield, AlertTriangle } from 'lucide-react';
import { GlobalNotification } from '../components/GlobalNotification';
import { useNotificationContext } from '../contexts/NotificationContext';
import { environment } from '../config/environment';
import {
  ADMIN_MAGIC_LINK_EXPIRED_MESSAGE,
  ADMIN_MAGIC_LINK_EXPIRY_HELP,
  shouldPromptAdminTokenContinue,
} from '../utils/adminMagicLink';

export const AdminPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, sessionExpired, verifyAdminMagicLink, adminSignOut } = useAdminAuth();
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [tokenConsumed, setTokenConsumed] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const { notifications, removeNotification } = useNotificationContext();

  const token = searchParams.get('token');
  const showContinueGate = shouldPromptAdminTokenContinue(token, user, tokenConsumed);

  // IS-1697: only verify on explicit user click so email scanners cannot burn one-time tokens
  const handleContinueWithToken = async () => {
    if (!token || tokenConsumed || verifyingToken) {
      return;
    }

    setVerifyingToken(true);
    setTokenConsumed(true);

    try {
      await verifyAdminMagicLink(token);
      navigate('/admin', { replace: true });
    } catch (error) {
      console.error('Error verifying admin magic link:', error);
      setTokenError(
        error instanceof Error ? error.message : ADMIN_MAGIC_LINK_EXPIRED_MESSAGE
      );
      // Keep ?token= in the URL on failure so a retry with a new link request is clear;
      // strip only after a successful sign-in above.
      navigate('/admin', { replace: true });
    } finally {
      setVerifyingToken(false);
    }
  };

  // Show loading state while checking authentication
  if (loading || verifyingToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">
            {verifyingToken ? 'Verifying your admin access...' : 'Checking authentication...'}
          </p>
        </div>
      </div>
    );
  }

  // Click-to-continue gate before consuming the one-time token (IS-1697)
  if (showContinueGate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Continue to Admin Portal</h3>
          <p className="text-sm text-gray-500 mb-6">
            Click below to sign in with your secure access link. This helps prevent email security
            scanners from using the link before you do.
          </p>
          <button
            type="button"
            onClick={handleContinueWithToken}
            className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Continue to Admin Portal
          </button>
        </div>
      </div>
    );
  }

  // Expired or invalid magic link — show message and option to request a new link (IS-1436)
  if (tokenError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto px-4 pt-12 pb-6">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {tokenError}
              </h3>
              <p className="text-sm text-gray-500">
                {ADMIN_MAGIC_LINK_EXPIRY_HELP}
              </p>
            </div>
          </div>
        </div>
        <AdminLogin embedded />
      </div>
    );
  }

  // Show login form if not authenticated or session expired
  if (!user || sessionExpired) {
    return <AdminLogin />;
  }

  // View a project using the admin's existing session.
  // Admins have Firestore read access to every project, so we navigate
  // straight to the project page instead of minting a one-time customer
  // magic link. Signing in as the customer would overwrite the shared
  // Firebase Auth session in this browser and lock the admin out.
  const handleViewProject = (jiraKey: string) => {
    const projectUrl = environment.useEmulators
      ? `http://localhost:5173/project/${jiraKey}`
      : `https://ca-onboarding.idealss.net/project/${jiraKey}`;
    window.open(projectUrl, '_blank');
  };

  const handleLogout = async () => {
    try {
      await adminSignOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Admin Portal</h1>
                <p className="text-sm text-gray-600">Onboarding Management Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                <p className="text-xs text-gray-500">Admin Access</p>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminDashboard onViewProject={handleViewProject} />
      </main>
      
      <GlobalNotification 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
    </div>
  );
};
